import { useCallback, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WorkIcon from "@mui/icons-material/Work";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import "../../styles/Listing/listing-premium.css";
import { useNavigate } from "react-router-dom";
import { deleteRequest, postRequest } from "../../api/apiServices";
import { candidatesList, deleteCandidate } from "../../api/apiList";
import Loading from "../../utils/Loading";
import PaginationComponent from "../../utils/PaginationComponent";
import debounce from "lodash/debounce";
import Toast from "../../utils/Toast";
import ConfirmationDialog from "../../components/DailogBox";
import { roleBasedRedirection } from "../../utils/helper";

interface jobDetails {
  id: number;
  jobTitle: string;
}

interface Candidates {
  id: number;
  jobDetails: jobDetails;
  phone: string;
  email: string;
  name: string;
}

const CandidateListing = () => {
  const navigate = useNavigate();
  const [candicate, setCandicate] = useState<Candidates[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loader, setLoader] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUpdate = (id: number) => {
    navigate(roleBasedRedirection(`/candidates/update-candidate/${id}`));
  };

  const handleDeleteClick = (id: number) => {
    setSelectedCandidate(id);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedCandidate(null);
  };

  const handleDelete = async () => {
    if (selectedCandidate !== null) {
      const response = await deleteRequest(deleteCandidate(selectedCandidate));
      Toast(response?.data?.statusMessage || "Candidate Delete Sucessfully...!");
      candidateList(page + 1, limit, searchTerm);
      setOpenDialog(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setSearchLoading(true);
    debouncedSearch(value);
  };

  const handleClick = () => {
    navigate(roleBasedRedirection("/candidates/create-candidate"));
  };

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      candidateList(1, limit, searchValue);
      setSearchLoading(false);
    }, 500),
    []
  );

  const candidateList = async (page: number, limit: number, search: string) => {
    setLoader(true);
    const response = await postRequest(candidatesList, {
      page,
      limit,
      search,
      searchColumns: ["phone", "email", "name", "jobPosition"],
    });
    if (response?.data?.success) {
      setCandicate(response?.data?.data?.candidates);
      setCount(response?.data?.data?.count);
      setPage(response?.data?.data?.page - 1);
      setHasNextPage(response?.data?.data?.hasNextPage);
      setHasPreviousPage(response?.data?.data?.hasPreviousPage);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (!searchLoading) {
      candidateList(page + 1, limit, searchTerm);
    }
  }, [page, limit]);

  return (
    <>
      <Box className="listing-page">
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <PersonIcon sx={{ fontSize: 14 }} />
                Candidates
              </Box>
              <h1 className="listing-hero-title">Candidates Management</h1>
              <p className="listing-hero-subtitle">
                Create and manage all your candidates
              </p>
            </Box>

            <Box className="listing-hero-actions">
              <button type="button" className="listing-hero-btn" onClick={handleClick}>
                <AddIcon sx={{ fontSize: 18 }} />
                Create New Candidate
              </button>

              <Box className="listing-hero-search">
                <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {loader ? (
          <Loading />
        ) : candicate && candicate.length > 0 ? (
          <>
            <Box className="listing-list">
              {candicate.map((candidateItem, index) => (
                <Box key={candidateItem.id} className="listing-card">
                  <Box className="listing-card-body">
                    <Box className="listing-card-info">
                      <Box className="listing-card-title-row">
                        <Box className="listing-card-index">
                          {index + 1 + page * limit}
                        </Box>
                        <Box>
                          <p className="listing-card-title">{candidateItem?.name}</p>
                          {candidateItem?.jobDetails?.jobTitle && (
                            <Box className="listing-card-subtitle">
                              <WorkIcon sx={{ fontSize: 15, color: "#56c6a9" }} />
                              {candidateItem?.jobDetails?.jobTitle}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Box className="listing-meta-row">
                        {candidateItem?.phone && (
                          <span className="listing-meta-chip">
                            <PhoneIcon />
                            {candidateItem.phone}
                          </span>
                        )}
                        {candidateItem?.email && (
                          <span className="listing-meta-chip">
                            <EmailIcon />
                            {candidateItem.email}
                          </span>
                        )}
                      </Box>
                    </Box>

                    <Box className="listing-actions">
                      <IconButton
                        className="listing-action-btn"
                        onClick={() => handleUpdate(candidateItem?.id)}
                        title="Edit"
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                      <IconButton
                        className="listing-action-btn listing-action-btn--danger"
                        onClick={() => handleDeleteClick(candidateItem?.id)}
                        title="Delete"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box className="listing-pagination-wrap">
              <PaginationComponent
                page={page}
                rowsPerPage={limit}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                count={count}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
              />
            </Box>
          </>
        ) : (
          <Box className="listing-empty">
            <Box className="listing-empty-icon">
              <PersonIcon />
            </Box>
            <h2 className="listing-empty-title">No Candidates Found</h2>
            <p className="listing-empty-text">
              {searchTerm
                ? `No candidates found matching "${searchTerm}". Try adjusting your search.`
                : "Create your first candidate profile to get started."}
            </p>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={openDialog}
        onConfirm={handleDelete}
        onCancel={handleClose}
        title="Confirm Deletion"
        message="Are you sure you want to permanently delete this candidate?"
        cancelButtonName="Cancel"
        confirmButtonName="Delete"
      />
    </>
  );
};

export default CandidateListing;
